import { useEffect, useRef, useState } from "react";
import {
    AnimatePresence,
    motion,
    useScroll,
    useTransform,
} from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { CATEGORIES, CAT_FR, FEATURED_IDS, PROJECTS, T } from "@/data/content";
import { FadeUp, SectionLabel, BlurBackdrop, Portal } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";
import ProjectModal from "@/components/ProjectModal";
import EmptySlot from "@/components/EmptySlot";
import ShareMediaButton from "@/components/ShareMediaButton";
import { ArrowUpRight, Clapperboard, Compass } from "lucide-react";

const LAYOUT = [
    { span: "md:col-span-7", ar: "aspect-video", off: "" },
    { span: "md:col-span-5", ar: "aspect-video", off: "md:mt-24" },
    { span: "md:col-span-5", ar: "aspect-video", off: "" },
    { span: "md:col-span-7", ar: "aspect-video", off: "md:mt-24" },
    { span: "md:col-span-6", ar: "aspect-video", off: "" },
    { span: "md:col-span-6", ar: "aspect-video", off: "md:mt-24" },
];

const WorkMedia = ({ p, className = "" }) =>
    p.video ? (
        <video
            className={className}
            poster={p.img}
            muted
            loop
            playsInline
            preload="none"
            onMouseEnter={(e) => {
                const v = e.currentTarget;
                v.muted = true;
                v.play().catch(() => {});
            }}
            onMouseLeave={(e) => e.currentTarget.pause()}
        >
            <source src={p.video} type="video/mp4" />
            <source src={`${p.video.split(".mp4")[0]}.webm`} type="video/webm" />
        </video>
    ) : (
        <img src={p.img} alt={p.title} loading="lazy" className={className} />
    );

export default function Work() {
    const { lang } = useLang();
    const [cat, setCat] = useState("ALL");

    // filters cross pages via PAGES; same-page via scrollToId
    const handleCat = (c) => {
        if (c === "MUSIC VIDEOS") {
            scrollToId("#music-videos", -90);
            return;
        }
        if (c === "FASHION") {
            if (window.__goPage) window.__goPage("photo");
            setTimeout(() => {
                const el = document.querySelector("[data-testid='photo-group-overdyed']");
                if (!el) return;
                if (window.__lenis) {
                    window.__lenis.scrollTo(el, {
                        duration: 1.5,
                        easing: (t) => 1 - Math.pow(1 - t, 4),
                    });
                } else {
                    el.scrollIntoView({ behavior: "smooth" });
                }
            }, 900);
            return;
        }
        if (c === "PHOTOGRAPHY") {
            if (window.__goPage) window.__goPage("photo");
            setTimeout(() => scrollToId("#photography"), 400);
            return;
        }
        setCat(c);
    };
    const [active, setActive] = useState(null);
    const headRef = useRef(null);
    const secRef = useRef(null);

    const { scrollYProgress: wmProg } = useScroll({
        target: secRef,
        offset: ["start end", "end start"],
    });
    const wy = useTransform(wmProg, [0, 1], ["-6%", "10%"]);

    const { scrollYProgress } = useScroll({
        target: headRef,
        offset: ["start end", "end start"],
    });
    const x = useTransform(scrollYProgress, [0, 1], [80, -120]);

    const featured = FEATURED_IDS.map((id) => PROJECTS.find((p) => p.id === id)).filter(Boolean);
    const filtered =
        cat === "ALL" ? featured : PROJECTS.filter((p) => p.category === cat);

    return (
        <section
            id="work"
            ref={secRef}
            data-testid="work-section"
            className="relative overflow-hidden bg-[#101014] py-24 text-cream md:py-36"
        >
            <BlurBackdrop src="/videos/work-sakura.jpg" tint="bg-[#101014]/70" />
            <motion.div
                aria-hidden="true"
                data-testid="work-watermark"
                style={{ y: wy }}
                className="pointer-events-none absolute -right-[10%] -top-[6%] z-0 select-none font-display text-[42vw] font-extrabold leading-none text-outline opacity-70 md:text-[30rem]"
            >
                G.
            </motion.div>
            <div className="relative z-10 mx-auto max-w-[1600px] px-5 md:px-10">
                <SectionLabel
                    en={tx(lang, T.work.label)}
                    fr={tx(lang, T.work.label)}
                />

                <div ref={headRef} className="mt-8 overflow-hidden md:mt-10">
                    <motion.h2
                        style={{ x }}
                        data-testid="work-kinetic-title"
                        className="whitespace-nowrap font-display text-[clamp(2.8rem,9vw,8.5rem)] font-extrabold uppercase leading-none tracking-[-0.02em]"
                    >
                        {tx(lang, T.work.title)}
                        <span className="text-violet">.</span>
                    </motion.h2>
                </div>

                {PROJECTS.length > 0 && (
                    <FadeUp delay={0.1} className="mt-10 md:mt-14">
                        <div
                            className="flex flex-wrap gap-2 md:gap-3"
                            data-testid="work-category-filters"
                        >
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c}
                                    data-testid={`work-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    onClick={() => handleCat(c)}
                                    className={`rounded-full border px-4 py-2 font-mono text-[10px] tracking-[0.2em] transition-all duration-300 md:px-5 md:text-[11px] ${
                                        cat === c
                                            ? "border-cream bg-cream text-ink"
                                            : "border-white/20 text-cream/60 hover:border-cream hover:text-cream"
                                    }`}
                                >
                                    {lang === "fr" ? CAT_FR[c] : c}
                                </button>
                            ))}
                        </div>
                    </FadeUp>
                )}
                <FadeUp delay={0.15} className="mt-6">
                    <p className="font-mono text-[9px] tracking-[0.3em] text-cream/40 md:text-[10px]">
                        {tx(lang, T.work.note)}
                    </p>
                </FadeUp>

                {cat === "CREATIVE DIRECTION" ? (
                    <FadeUp delay={0.1} className="mt-12 md:mt-16">
                        <div
                            data-testid="work-vision"
                            className="max-w-3xl border border-white/15 bg-white/5 p-8 backdrop-blur-sm md:p-14"
                        >
                            <p className="flex items-center gap-3 font-mono text-[9px] tracking-[0.35em] text-violet md:text-[10px]">
                                <Compass size={12} />
                                {tx(lang, T.work.visionLabel)}
                            </p>
                            <p className="mt-6 font-serif text-lg italic leading-relaxed text-cream/85 md:text-2xl">
                                {tx(lang, T.work.vision)}
                            </p>
                        </div>
                    </FadeUp>
                ) : filtered.length === 0 ? (
                    <FadeUp delay={0.1} className="mt-12 md:mt-16">
                        <EmptySlot
                            testid="work-empty-slot"
                            icon={Clapperboard} dark
                            label={tx(lang, T.empty.work)}
                            aspect="aspect-[16/9] md:aspect-[21/9]"
                        />
                    </FadeUp>
                ) : (
                <motion.div
                    key={cat}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8"
                >
                    {filtered.map((p, i) => {
                        const L = LAYOUT[i % LAYOUT.length];
                        return (
                            <div key={p.id} className={`group relative ${L.span} ${L.off}`}>
                            <motion.button
                                data-testid={`project-card-${p.id}`}
                                data-cursor="VIEW"
                                onClick={() => setActive(p)}
                                initial={{ opacity: 0, y: 70, rotate: i % 2 ? 1.6 : -1.6, filter: "blur(10px)" }}
                                whileInView={{ opacity: 1, y: 0, rotate: 0, filter: "blur(0px)" }}
                                viewport={{ once: true, amount: 0.15 }}
                                transition={{
                                    duration: 1,
                                    delay: (i % 3) * 0.09,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                className="group relative block w-full overflow-hidden text-left"
                            >
                                <div className={`relative w-full overflow-hidden ${L.ar}`}>
                                    <WorkMedia
                                        p={p}
                                        className="h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.06]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/10 to-ink/20 opacity-90 transition-opacity duration-500 group-hover:opacity-100" />
                                    <div className="absolute inset-x-0 top-0 flex items-center justify-between p-5 font-mono text-[9px] tracking-[0.25em] text-cream/90 md:text-[10px]">
                                        <span>{p.category}</span>
                                        <span>{p.year}</span>
                                    </div>
                                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5 md:p-7">
                                        <div>
                                            <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-cream transition-transform duration-500 group-hover:translate-x-2 md:text-3xl">
                                                {p.title}
                                            </h3>
                                            {p.client && (
                                                <p className="mt-2 font-mono text-[9px] tracking-[0.25em] text-cream/70 md:text-[10px]">
                                                    {p.client}
                                                </p>
                                            )}
                                        </div>
                                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-cream/40 text-cream opacity-0 transition-all duration-500 group-hover:opacity-100 group-hover:rotate-45">
                                            <ArrowUpRight size={14} />
                                        </span>
                                    </div>
                                </div>
                            </motion.button>
                            {/* partage réseaux — overlay (hors du bouton carte) */}
                            <div className="absolute bottom-4 right-4 z-10 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100" onClick={(e) => e.stopPropagation()}>
                                <ShareMediaButton
                                    kind="video"
                                    src={p.video || p.img}
                                    poster={p.img}
                                    title={`${p.title}${p.client ? ` — ${p.client}` : ""}`}
                                    target="#work"
                                    testid={`share-work-${p.id}`}
                                />
                            </div>
                            </div>
                        );
                    })}
                    {cat === "ALL" && (
                        <FadeUp delay={0.1} className="md:col-span-12 md:mt-16">
                            <div className="flex justify-center">
                                <button
                                    data-testid="work-more-btn"
                                    onClick={() => window.__goPage?.("videos")}
                                    className="group flex items-center gap-4 border border-white/20 px-8 py-4 font-mono text-[11px] tracking-[0.3em] text-cream/80 transition-all duration-500 hover:border-violet hover:bg-violet hover:text-cream"
                                >
                                    {tx(lang, T.work.more)}
                                    <ArrowUpRight
                                        size={14}
                                        className="transition-transform duration-500 group-hover:rotate-45"
                                    />
                                </button>
                            </div>
                        </FadeUp>
                    )}
                </motion.div>
                )}
            </div>

            <Portal><AnimatePresence>
                {active && (
                    <ProjectModal
                        project={active}
                        onClose={() => setActive(null)}
                    />
                )}
            </AnimatePresence></Portal>
        </section>
    );
}
